/**
 * features/71b/export71b.ts — MER Algo Footprints v7.1B — Phase 7.1B
 *
 * Lightweight local export utility for Phase 7.1B operator data.
 * Client-side only — no server calls, no auth, no cloud persistence.
 *
 * Supports export of:
 *   - Journal71A activity log (CSV / JSON)
 *   - Discipline score breakdown (JSON)
 *   - Individual replay snapshot (JSON)
 *
 * All exported data is read-only presentation of stored journal entries.
 * Export never feeds back into engine, ranking, or scoring.
 */

import { Journal71AEntry } from '../../types';

// ─── Download helper ──────────────────────────────────────────────────────────

function downloadBlob(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function timestamp(): string {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

// ─── Journal CSV export ───────────────────────────────────────────────────────

const CSV_HEADERS = [
  'Timestamp', 'Symbol', 'Action', 'Setup Type', 'Direction',
  'Score', 'Confidence', 'Regime', 'Gamma Status', 'Outcome', 'Note',
] as const;

function entryToCSVRow(e: Journal71AEntry): string {
  const fmt = (v: string | number | null | undefined) => {
    if (v === null || v === undefined || v === '') return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const ts = new Date(e.ts).toISOString();
  const action = e.actionType.replace(/_/g, ' ').toUpperCase();
  return [
    fmt(ts), fmt(e.symbol), fmt(action),
    fmt(e.setupType?.replace(/_/g, ' ') ?? ''),
    fmt(e.direction ?? ''),
    fmt(e.score ?? ''),
    fmt(e.confidence ?? ''),
    fmt(e.regime ?? ''),
    fmt(e.gammaStatus ?? ''),
    fmt(e.outcome ?? ''),
    fmt(e.note),
  ].join(',');
}

export function exportJournalCSV(entries: Journal71AEntry[]): void {
  if (!entries.length) return;
  const rows = [
    CSV_HEADERS.join(','),
    ...[...entries].sort((a, b) => b.ts - a.ts).map(entryToCSVRow),
  ];
  downloadBlob(rows.join('\n'), `MER-activity-log-${timestamp()}.csv`, 'text/csv');
}

// ─── Journal JSON export ──────────────────────────────────────────────────────

export function exportJournalJSON(entries: Journal71AEntry[]): void {
  if (!entries.length) return;
  const payload = {
    exportedAt:   new Date().toISOString(),
    version:      '7.1B',
    source:       'MER Algo Footprints — Activity Log',
    entryCount:   entries.length,
    entries:      [...entries].sort((a, b) => b.ts - a.ts),
  };
  downloadBlob(
    JSON.stringify(payload, null, 2),
    `MER-activity-log-${timestamp()}.json`,
    'application/json',
  );
}

// ─── Discipline score JSON export ─────────────────────────────────────────────

export interface DisciplineExportPayload {
  exportedAt:   string;
  version:      string;
  source:       string;
  cappedScore:  number;
  rawScore:     number;
  grade:        string;
  entryCount:   number;
  rules: Array<{
    id: string; label: string; detail: string;
    value: number; count: number; total: number;
  }>;
  observations: string[];
}

export function exportDisciplineJSON(payload: DisciplineExportPayload): void {
  downloadBlob(
    JSON.stringify({ ...payload, exportedAt: new Date().toISOString() }, null, 2),
    `MER-discipline-score-${timestamp()}.json`,
    'application/json',
  );
}

// ─── Replay snapshot JSON export ──────────────────────────────────────────────

export function exportReplaySnapshotJSON(entry: Journal71AEntry): void {
  const payload = {
    exportedAt:      new Date().toISOString(),
    version:         '7.1B',
    source:          'MER Algo Footprints — Replay Snapshot',
    disclaimer:      'Historical read-only snapshot captured at log time.',
    loggedAt:        new Date(entry.ts).toISOString(),
    symbol:          entry.symbol,
    action:          entry.actionType.replace(/_/g, ' ').toUpperCase(),
    setupType:       entry.setupType?.replace(/_/g, ' ') ?? null,
    direction:       entry.direction,
    score:           entry.score,
    confidence:      entry.confidence,
    regime:          entry.regime,
    gammaStatus:     entry.gammaStatus,
    outcome:         entry.outcome,
    operatorNote:    entry.note || null,
    rankedSnapshot:  entry.rankedSnapshot ?? null,
  };
  downloadBlob(
    JSON.stringify(payload, null, 2),
    `MER-snapshot-${entry.symbol}-${timestamp()}.json`,
    'application/json',
  );
}
